import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Printer, FileSpreadsheet } from 'lucide-react';
import { API, downloadPdf } from '@/utils/api';
import { fmtRange } from '@/components/agenda/agendaUtils';
import { SheetLink, SHEET_LINK_CSS } from '@/components/agenda/SheetLink';

const Y = '#FACC15';
const BG = '#0E1116';
const CARD = '#181C23';
const STATUS = { scheduled: 'Planifié', in_progress: 'En cours', completed: 'Terminé', task: 'Tâche atelier' };
const COLS = '15mm 30mm 50mm 30mm 50mm 1fr 27mm';

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Archivo:wght@500;600;700;800;900&display=swap');
@page { size: A4 landscape; margin: 0; }
@media print {
  html, body { background: ${BG} !important; }
  body * { visibility: hidden; }
  #period-sheet, #period-sheet * { visibility: visible; }
  #period-sheet { position: absolute; left: 0; top: 0; box-shadow: none !important; }
  .no-print { display: none !important; }
}
#period-sheet, #period-sheet * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.pl-row { break-inside: avoid; }
.pl-day { break-after: avoid; }
`;

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const lastThreeMonths = () => {
  const now = new Date();
  return [iso(new Date(now.getFullYear(), now.getMonth() - 3, 1)), iso(new Date(now.getFullYear(), now.getMonth(), 0))];
};
const dayLabel = (d) => new Date(`${d}T12:00:00`).toLocaleDateString('fr-CH', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
const short = (d) => new Date(`${d}T12:00:00`).toLocaleDateString('fr-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });

const Row = ({ it }) => {
  const isTask = it.kind === 'task';
  const status = isTask ? STATUS.task : `${STATUS[it.status] || it.status}${it.client_confirmed ? ' · confirmé' : ''}`;
  return (
    <div className="pl-row" data-testid={`period-row-${it.id}`} style={{ display: 'grid', gridTemplateColumns: COLS, gap: '3mm', alignItems: 'center', background: CARD, borderLeft: `1mm solid ${isTask ? '#64748B' : Y}`, borderRadius: '1.2mm', padding: '2mm 3mm', marginTop: '1.4mm', fontSize: '3.1mm', color: '#fff' }}>
      <div style={{ color: '#9AA3AF', fontWeight: 700 }}>{it.date.slice(8, 10)}.{it.date.slice(5, 7)}</div>
      <div style={{ color: Y, fontWeight: 800 }}>{it.ranges.map(r => fmtRange(r[0], r[1])).join(' / ')}{it.end_date && <div style={{ color: '#9AA3AF', fontSize: '2.6mm' }}>→ {short(it.end_date)}</div>}</div>
      <div style={{ fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}><SheetLink to={it.client_id && `/admin/clients/${it.client_id}`} title="Ouvrir la fiche client" testid={`period-client-link-${it.id}`}>{it.client}</SheetLink></div>
      <div style={{ color: '#CBD5E1' }}>{it.phone || '—'}</div>
      <div style={{ color: '#CBD5E1', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.vehicle}</div>
      <div style={{ fontWeight: 600 }}><SheetLink to={it.work_order_id && `/admin/work-orders/${it.work_order_id}`} title="Ouvrir la fiche atelier" testid={`period-work-link-${it.id}`}>{it.work || '—'}</SheetLink></div>
      <div style={{ fontSize: '2.7mm', fontWeight: 700, color: it.status === 'completed' ? '#86EFAC' : isTask ? '#94A3B8' : Y }}>{status}</div>
    </div>
  );
};

const Sheet = ({ data, logo }) => {
  const days = data.items.reduce((acc, it) => { (acc[it.date] = acc[it.date] || []).push(it); return acc; }, {});
  const count = data.items.filter(i => i.kind === 'appointment').length;
  return (
    <div id="period-sheet" data-testid="period-sheet" style={{ width: '297mm', minHeight: '194mm', background: BG, fontFamily: "'Archivo', 'Helvetica Neue', Arial, sans-serif", padding: '0 11mm 8mm', boxShadow: '0 20px 60px rgba(0,0,0,.35)' }}>
      <div style={{ height: '2mm', background: Y, margin: '0 -11mm' }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: '6mm', padding: '6mm 0 4mm', borderBottom: '0.3mm solid #2A303A' }}>
        <div style={{ width: '26mm', height: '20mm', background: '#000', borderRadius: '1.5mm', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
          {logo && <img src={logo} alt="logo" style={{ maxWidth: '92%', maxHeight: '92%', objectFit: 'contain' }} />}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ color: '#fff', fontWeight: 800, fontSize: '5mm', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{data.company_name || 'Garage'}</div>
          <div style={{ color: '#fff', fontWeight: 900, fontSize: '9.5mm', lineHeight: 1.1, whiteSpace: 'nowrap' }}>LISTE DES RENDEZ-VOUS</div>
        </div>
        <div>
          <div style={{ color: Y, fontWeight: 800, fontSize: '4mm', letterSpacing: '0.08em' }}>PÉRIODE</div>
          <div style={{ color: '#fff', fontWeight: 900, fontSize: '5.5mm' }} data-testid="period-range">{short(data.date_from)} – {short(data.date_to)}</div>
          <div style={{ color: '#9AA3AF', fontSize: '3mm', marginTop: '1mm' }} data-testid="period-count">{count} rendez-vous</div>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: COLS, gap: '3mm', background: Y, borderRadius: '1.2mm', padding: '2mm 3mm', marginTop: '4mm', color: '#111', fontWeight: 800, fontSize: '2.7mm', letterSpacing: '0.06em' }}>
        <div>DATE</div><div>HORAIRES</div><div>CLIENT</div><div>TÉLÉPHONE</div><div>VÉHICULE</div><div>TRAVAUX</div><div>STATUT</div>
      </div>
      {Object.keys(days).length === 0 && <div style={{ color: '#9AA3AF', fontSize: '4mm', padding: '8mm 3mm' }}>Aucun rendez-vous sur cette période</div>}
      {Object.entries(days).map(([d, list]) => (
        <div key={d}>
          <div className="pl-day" style={{ color: Y, fontWeight: 800, fontSize: '3.3mm', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: '4mm' }}>{dayLabel(d)}</div>
          {list.map(it => <Row key={it.id} it={it} />)}
        </div>
      ))}
    </div>
  );
};

export default function AgendaPeriodPrintPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [defFrom, defTo] = lastThreeMonths();
  const from = params.get('from') || defFrom;
  const to = params.get('to') || defTo;
  const [data, setData] = useState(null);
  const [logo, setLogo] = useState('');

  useEffect(() => {
    setData(null);
    axios.get(`${API}/agenda/period-list`, { params: { date_from: from, date_to: to } })
      .then(r => setData(r.data)).catch(() => setData({ date_from: from, date_to: to, items: [], company_name: '' }));
  }, [from, to]);

  useEffect(() => {
    let url = '';
    axios.get(`${API}/agenda/planning-logo`, { responseType: 'blob' }).then(r => { url = URL.createObjectURL(r.data); setLogo(url); }).catch(() => {});
    return () => url && URL.revokeObjectURL(url);
  }, []);

  return (
    <div className="min-h-screen bg-slate-200 py-6 flex flex-col items-center gap-4" data-testid="agenda-period-print-page">
      <style>{CSS + SHEET_LINK_CSS}</style>
      <div className="no-print flex flex-wrap items-center gap-3 bg-white rounded-full shadow px-4 py-2">
        <button onClick={() => navigate('/admin/agenda')} className="flex items-center gap-1 text-sm font-medium text-slate-700 hover:text-slate-950" data-testid="period-back-btn"><ArrowLeft className="w-4 h-4" />Agenda</button>
        <span className="text-sm text-slate-500">Du</span>
        <input type="date" value={from} onChange={e => setParams({ from: e.target.value, to })} className="border rounded-md px-2 py-1 text-sm" data-testid="period-from-input" />
        <span className="text-sm text-slate-500">au</span>
        <input type="date" value={to} onChange={e => setParams({ from, to: e.target.value })} className="border rounded-md px-2 py-1 text-sm" data-testid="period-to-input" />
        <button onClick={() => downloadPdf(`${API}/agenda/period-list.xlsx?date_from=${from}&date_to=${to}`, `agenda_${from}_${to}.xlsx`)} className="flex items-center gap-2 border border-emerald-600 text-emerald-700 font-bold text-sm rounded-full px-4 py-1.5 hover:bg-emerald-50 transition-colors" data-testid="period-excel-btn"><FileSpreadsheet className="w-4 h-4" />Excel</button>
        <button onClick={() => window.print()} disabled={!data} className="flex items-center gap-2 bg-[#0E1116] text-[#FACC15] font-bold text-sm rounded-full px-4 py-1.5 hover:bg-black transition-colors disabled:opacity-50" data-testid="period-print-btn"><Printer className="w-4 h-4" />Imprimer</button>
      </div>
      {data ? <Sheet data={data} logo={logo} /> : <div className="text-slate-500 py-20">Chargement…</div>}
    </div>
  );
}
